/*eslint max-lines-per-function: ["error", 280]*/
import React, { useMemo, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import { BlockadeMonthSearch } from '../../components/BlockadeMonth/BlockadeMonthSearch';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateBlockadeMonthDto,
  CreateUpdateBlockadeMonthExceptionDto,
  GetBlockadeMonthDto,
  GetBlockadeMonthExceptionDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { BlockadeMonthEditor } from '../../components/BlockadeMonth/BlockadeMonthEditor';
import {
  convert,
  validate,
} from '../../../common/pokCore/contexts/BlockadeMonthContext';
import {
  convert as convertException,
  validate as validateException,
} from '../../../common/pokCore/contexts/BlockadeMonthExceptionContext';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { BlockadeMonthExceptionSearch } from '../../components/BlockadeMonthException/BlockadeMonthExceptionSearch';
import { BlockadeMonthExceptionEditor } from '../../components/BlockadeMonthException/BlockadeMonthExceptionEditor';

enum BlockadesTabs {
  GLOBAL_BLOCKADES = 'blockadesGlobal',
  EXCEPTIONS_BLOCKADES = 'blockadesExceptions',
  HISTORY_EXCEPTIONS = 'historyExceptionBlockades',
}

export const BlockadeView: React.FC = () => {
  const [blockadeMonth, setBlockadeMonth] = useState<GetBlockadeMonthDto>();
  const [blockadeMonthException, setBlockadeMonthException] =
    useState<GetBlockadeMonthExceptionDto>();
  const [refresh, setRefresh] = useState(false);
  const [showBlockadeMonth, setShowBlockadeMonth] = useState(false);
  const [showBlockadeException, setShowBlockadeException] = useState(false);
  const pok = usePokCore();
  const { t, tk } = useTranslation('projectLng');

  const handleAddBlockadeMonth = () => {
    setShowBlockadeMonth(true);
    setShowBlockadeException(false);
    setBlockadeMonth(undefined);
    setBlockadeMonthException(undefined);
  };

  const handleAddBlockadeException = () => {
    setShowBlockadeMonth(false);
    setShowBlockadeException(true);
    setBlockadeMonth(undefined);
    setBlockadeMonthException(undefined);
  };

  const handleClickBlockadeMonth = async (id: string) => {
    setBlockadeMonth(await pok.blockades.getById(id));
    setBlockadeMonthException(undefined);
    setShowBlockadeMonth(true);
    setShowBlockadeException(false);
  };

  const handleClickBlockadeException = async (id: string) => {
    setBlockadeMonth(undefined);
    setBlockadeMonthException(await pok.blockadesException.getById(id));
    setShowBlockadeMonth(false);
    setShowBlockadeException(true);
  };

  const addEditBlockadeMonth = async (
    blockadeCU: CreateUpdateBlockadeMonthDto,
    id?: string,
  ) => {
    const status = await validate(blockadeCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.blockades.update(id, blockadeCU);
        } else {
          await pok.blockades.create(blockadeCU);
        }
        setShowBlockadeMonth(false);

        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const addEditBlockadeException = async (
    blockadeExceptionCU: CreateUpdateBlockadeMonthExceptionDto,
    id?: string,
  ) => {
    const status = await validateException(blockadeExceptionCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.blockadesException.update(id, blockadeExceptionCU);
        } else {
          await pok.blockadesException.create(blockadeExceptionCU);
        }
        setShowBlockadeException(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClose = (todoRefresh?: boolean) => {
    setBlockadeMonth(undefined);
    setBlockadeMonthException(undefined);
    setShowBlockadeMonth(false);
    setShowBlockadeException(false);
    if (todoRefresh) {
      setRefresh(!refresh);
    }
  };

  const getData = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.blockades.getAllByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.blockades, t, tk],
  );

  const getDataExceptions = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.blockadesException.getActiveByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.blockadesException, t, tk],
  );

  const getDataAllExceptions = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.blockadesException.getAllByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [pok.blockadesException, t, tk],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.projects.monthBlockades)}
        </Card.Title>

        <TabsHistory
          activeKey={BlockadesTabs.GLOBAL_BLOCKADES}
          id="product"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={BlockadesTabs.GLOBAL_BLOCKADES}
            title={t(tk.projects.globalBlockades)}
            className="m-3"
          >
            <Button
              className="mb-4"
              variant="outline-primary"
              onClick={handleAddBlockadeMonth}
            >
              {t(tk.projects.newMonthBlockade)}
            </Button>
            <BlockadeMonthSearch
              getData={getData}
              getCount={pok.blockades.getAllCount}
              onRowClick={handleClickBlockadeMonth}
              refresh={refresh}
              showFilter={true}
            />
            <BlockadeMonthEditor
              id={blockadeMonth?.id}
              blockadeMonth={convert(blockadeMonth, pok.getCompanyId())}
              show={showBlockadeMonth}
              handleClose={handleClose}
              handleCloseAndRefresh={() => handleClose(true)}
              onCompleted={addEditBlockadeMonth}
            />
          </Tab>
          <Tab
            eventKey={BlockadesTabs.EXCEPTIONS_BLOCKADES}
            title={t(tk.projects.exceptionBlockades)}
            className="m-3"
          >
            <Button
              className="mb-4"
              variant="outline-primary"
              onClick={handleAddBlockadeException}
            >
              {t(tk.projects.newMonthUnblockade)}
            </Button>
            <BlockadeMonthExceptionSearch
              getData={getDataExceptions}
              getCount={pok.blockadesException.getActiveCount}
              onRowClick={handleClickBlockadeException}
              refresh={refresh}
              showFilter={true}
            />
          </Tab>
          <Tab
            eventKey={BlockadesTabs.HISTORY_EXCEPTIONS}
            title={t(tk.projects.historyExceptionBlockades)}
            className="m-3"
          >
            <BlockadeMonthExceptionSearch
              getData={getDataAllExceptions}
              getCount={pok.blockadesException.getAllCount}
              onRowClick={handleClickBlockadeException}
              refresh={refresh}
              showFilter={true}
            />
          </Tab>
        </TabsHistory>
        <BlockadeMonthExceptionEditor
          id={blockadeMonthException?.id}
          blockadeMonthException={convertException(blockadeMonthException)}
          show={showBlockadeException}
          handleClose={handleClose}
          handleCloseAndRefresh={() => handleClose(true)}
          onCompleted={addEditBlockadeException}
          readonly={
            !blockadeMonthException?.active && !!blockadeMonthException?.id
          }
        />
      </Card.Body>
    </Card>
  );
};
